// WebAuthn Biometric Authentication Utilities
import api from './api';

// Check if WebAuthn is supported
export const isWebAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined;
};

// Check if platform authenticator is available (Face ID, Touch ID, Windows Hello)
export const isPlatformAuthenticatorAvailable = async () => {
  if (!isWebAuthnSupported()) return false;
  
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

// Convert base64url to ArrayBuffer
const base64urlToBuffer = (base64url) => {
  if (!base64url || typeof base64url !== 'string') {
    console.error('Invalid base64url input:', base64url);
    return new ArrayBuffer(0);
  }
  
  try {
    const padding = '='.repeat((4 - base64url.length % 4) % 4);
    const base64 = (base64url + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray.buffer;
  } catch (error) {
    console.error('base64urlToBuffer error:', error);
    return new ArrayBuffer(0);
  }
};

// Convert ArrayBuffer to base64url
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const byte of bytes) {
    str += String.fromCharCode(byte);
  }
  return window.btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

// Register a new biometric credential
export const registerBiometric = async (userId) => {
  if (!isWebAuthnSupported()) {
    return { success: false, error: 'المتصفح لا يدعم البصمة' };
  }

  try {
    // Step 1: Get registration options from server
    const beginResponse = await api.post('/webauthn/register/begin', { user_id: userId });
    let options = beginResponse.data.options;
    
    // Parse options if it's a string (backend returns JSON string)
    if (typeof options === 'string') {
      try {
        options = JSON.parse(options);
      } catch (parseError) {
        console.error('Failed to parse options:', parseError);
        return { success: false, error: 'خطأ في بيانات التسجيل' };
      }
    }
    
    // Validate required fields
    if (!options || !options.challenge || !options.user || !options.user.id) {
      console.error('Invalid registration options:', options);
      return { success: false, error: 'بيانات التسجيل غير صالحة' };
    }

    // Convert challenge and user.id from base64url to ArrayBuffer
    const publicKeyOptions = {
      challenge: base64urlToBuffer(options.challenge),
      rp: options.rp,
      user: {
        id: base64urlToBuffer(options.user.id),
        name: options.user.name,
        displayName: options.user.displayName,
      },
      pubKeyCredParams: options.pubKeyCredParams,
      timeout: options.timeout || 60000,
      attestation: options.attestation || 'none',
      authenticatorSelection: options.authenticatorSelection,
      excludeCredentials: (options.excludeCredentials || []).map(cred => ({
        ...cred,
        id: base64urlToBuffer(cred.id),
      })),
    };

    // Step 2: Create credential using browser API
    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    });

    if (!credential) {
      return { success: false, error: 'تم إلغاء التسجيل' };
    }

    // Step 3: Send credential to server for verification
    const completeResponse = await api.post('/webauthn/register/complete', {
      user_id: userId,
      credential_id: bufferToBase64url(credential.rawId),
      raw_id: bufferToBase64url(credential.rawId),
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        attestationObject: bufferToBase64url(credential.response.attestationObject),
      },
      type: credential.type,
    });

    return { 
      success: true, 
      message: completeResponse.data.message,
      credentialId: completeResponse.data.credential_id 
    };

  } catch (error) {
    console.error('Biometric registration error:', error);
    
    if (error.name === 'NotAllowedError') {
      return { success: false, error: 'تم رفض الإذن أو إلغاء العملية' };
    }
    if (error.name === 'InvalidStateError') {
      return { success: false, error: 'البصمة مسجلة مسبقاً' };
    }
    
    return { 
      success: false, 
      error: error.response?.data?.detail || error.message || 'فشل تسجيل البصمة' 
    };
  }
};

// Authenticate with biometric
export const authenticateWithBiometric = async (userId) => {
  if (!isWebAuthnSupported()) {
    return { success: false, error: 'المتصفح لا يدعم البصمة' };
  }

  try {
    // Step 1: Get authentication options from server
    const beginResponse = await api.post('/webauthn/authenticate/begin', { user_id: userId });
    let options = beginResponse.data.options;
    
    // Parse options if it's a string
    if (typeof options === 'string') {
      try {
        options = JSON.parse(options);
      } catch (parseError) {
        console.error('Failed to parse options:', parseError);
        return { success: false, error: 'خطأ في بيانات التحقق' };
      }
    }
    
    // Validate required fields
    if (!options || !options.challenge) {
      console.error('Invalid authentication options:', options);
      return { success: false, error: 'بيانات التحقق غير صالحة' };
    }

    // Convert challenge and allowCredentials from base64url to ArrayBuffer
    const publicKeyOptions = {
      challenge: base64urlToBuffer(options.challenge),
      timeout: options.timeout || 60000,
      rpId: options.rpId,
      userVerification: options.userVerification || 'preferred',
      allowCredentials: (options.allowCredentials || []).map(cred => ({
        ...cred,
        id: base64urlToBuffer(cred.id),
      })),
    };

    // Step 2: Get credential using browser API
    const credential = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    });

    if (!credential) {
      return { success: false, error: 'تم إلغاء تسجيل الدخول' };
    }

    // Step 3: Send credential to server for verification
    const completeResponse = await api.post('/webauthn/authenticate/complete', {
      user_id: userId,
      credential_id: bufferToBase64url(credential.rawId),
      raw_id: bufferToBase64url(credential.rawId),
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        authenticatorData: bufferToBase64url(credential.response.authenticatorData),
        signature: bufferToBase64url(credential.response.signature),
        userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null,
      },
      type: credential.type,
    });

    return { 
      success: true, 
      data: completeResponse.data 
    };

  } catch (error) {
    console.error('Biometric authentication error:', error);
    
    if (error.name === 'NotAllowedError') {
      return { success: false, error: 'تم رفض الإذن أو إلغاء العملية' };
    }
    
    return { 
      success: false, 
      error: error.response?.data?.detail || error.message || 'فشل التحقق من البصمة' 
    };
  }
};

// Check if user has biometric registered
export const checkBiometricAvailable = async (username) => {
  try {
    const response = await api.get(`/webauthn/check/${encodeURIComponent(username)}`);
    return response.data;
  } catch (error) {
    console.error('Check biometric error:', error);
    return { available: false, user_id: null };
  }
};

// Get user's registered credentials
export const getUserCredentials = async (userId) => {
  try {
    const response = await api.get(`/webauthn/credentials/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Get credentials error:', error);
    return { credentials: [], has_biometric: false };
  }
};

// Delete a credential
export const deleteCredential = async (credentialId) => {
  try {
    const response = await api.delete(`/webauthn/credentials/${credentialId}`);
    return { success: true, message: response.data.message };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || 'فشل حذف البصمة' };
  }
};

export default {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  registerBiometric,
  authenticateWithBiometric,
  checkBiometricAvailable,
  getUserCredentials,
  deleteCredential,
};
